import React, { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CasinoIcon from '@mui/icons-material/Casino';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {
    styled,
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    InputAdornment,
} from '@mui/material';

import { useTextInput, useNumberInput } from 'hooks/forms';
import { TextField, Dropdown } from 'components/ui/inputs';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { ActionButton } from 'components/ui/buttons';

import * as PromoteAPI from '../PromoteAPI';
import * as PromoteUtils from '../PromoteUtils';
import { useNotification } from 'context/notification';

export default function TrackingLinks({ hostId, eventId }) {
    const [sources, setSources] = useState([]);
    const [links, setLinks] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLink, setEditingLink] = useState(null);

    const { notify } = useNotification();

    useEffect(() => {
        // Fetch all Sources for this host AND
        // Fetch all Tracking Links for this event
        // We only show the user sources that don't yet have a link for this event
        Promise.all([
            PromoteAPI.getTrackingLinks(hostId, eventId),
            PromoteAPI.getTrackingSources(hostId, eventId),
        ])
            .then(([trackingLinksResponse, sourcesResponse]) => {
                if (trackingLinksResponse.error) {
                    throw trackingLinksResponse.error;
                }
                if (sourcesResponse.error) {
                    throw sourcesResponse.error;
                }

                const { trackingLinks } = trackingLinksResponse;
                const { sources } = sourcesResponse;

                const sourcesWithoutLinks = sources.filter(
                    (r) => !trackingLinks.some((link) => link.sourceId === r.id),
                );

                setLinks(trackingLinks);
                setSources(sourcesWithoutLinks);
            })
            .catch((error) => {
                console.error('Failed to fetch data:', error);
            });
    }, [hostId, eventId, refresh]);

    const openCreateDialog = () => {
        setDialogOpen(true);
        setEditingLink(null);
    };

    const openEditDialog = (link) => {
        setEditingLink(link);
        setDialogOpen(true);
    };

    const handleUpdate = () => {
        setDialogOpen(false);
        setEditingLink(null);
        setRefresh((prev) => prev + 1); // Trigger a refresh
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditingLink(null);
    };

    const copyToClipboard = async (value) => {
        navigator.clipboard
            .writeText(value)
            .then(() => {
                notify.info('Copied to clipboard!');
            })
            .catch(() => {
                notify.error('Unable to copy to clipboard');
            });
    };

    return (
        <Card>
            <CardHeader
                title="Tracking Links"
                subheader="Create and manage tracking links for your event. These links allow you to track where your customers are coming from when they visit your event page."
            />
            <CardContent>
                {links.length === 0 && (
                    <Typography color="textSecondary">No tracking links yet.</Typography>
                )}

                {links.length > 0 &&
                    links.map((link, idx) => (
                        <TrackingLink key={idx}>
                            <Grid container>
                                <Grid item xs={12} lg={8}>
                                    <Typography fontWeight={'bold'}>{link.sourceName}</Typography>
                                    <Typography variant="body2">{link.link}</Typography>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <Box
                                        display={'flex'}
                                        height={'100%'}
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        <ActionButton
                                            onClick={() => copyToClipboard(link.link)}
                                            startIcon={<FileCopyIcon />}
                                        >
                                            Copy Link
                                        </ActionButton>
                                        <ActionButton
                                            style={{ marginLeft: 8 }}
                                            onClick={() => openEditDialog(link)}
                                        >
                                            Edit
                                        </ActionButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </TrackingLink>
                    ))}
            </CardContent>

            <CardActions>
                <ActionButton startIcon={<AddIcon />} onClick={openCreateDialog}>
                    New Link
                </ActionButton>
            </CardActions>

            {dialogOpen && Boolean(editingLink) === false && (
                <TrackingLinkCreator
                    hostId={hostId}
                    eventId={eventId}
                    sources={sources}
                    onClose={handleClose}
                    onUpdate={handleUpdate}
                />
            )}

            {dialogOpen && Boolean(editingLink) === true && (
                <TrackingLinkEditor
                    hostId={hostId}
                    eventId={eventId}
                    editingLink={editingLink}
                    onClose={handleClose}
                    onUpdate={handleUpdate}
                />
            )}
        </Card>
    );
}

export function TrackingLinkCreator(props) {
    const { hostId, eventId, sources, onClose, onUpdate } = props;

    const { notify } = useNotification();

    const source = useNumberInput(); // Used when using an existing source
    const sourceName = useTextInput({ maxLength: 50 }); // Used when creating a new source
    const slug = useTextInput({ format: 'urlSafe', maxLength: 50 });

    const [sourceMode, setSourceMode] = useState(sources.length > 0 ? 'existing' : 'new');

    const randomizeSlug = () => {
        const randomCode = PromoteUtils.generateTrackingCode();
        slug.setValue(randomCode);
    };

    const handleSave = () => {
        let formError = false;

        if (slug.value.length === 0) {
            slug.setError('This field cannot be empty');
            formError = true;
        }

        if (sourceMode === 'existing' && !source.value) {
            source.setError('Please select a source');
            formError = true;
        }

        if (sourceMode === 'new' && sourceName.value.length === 0) {
            sourceName.setError('Please enter a source name');
            formError = true;
        }

        if (formError) {
            return;
        }

        const existingSourceId = sourceMode === 'existing' ? Number(source.value) : null;
        const newSourceName = sourceMode === 'new' ? sourceName.value : null;

        PromoteAPI.createTrackingLink(hostId, eventId, existingSourceId, newSourceName, slug.value)
            .then(({ code, error }) => {
                if (error) {
                    switch (code) {
                        case 'link_already_exists':
                            source.setError('This source already has a tracking link');
                            break;
                        case 'source_in_use':
                            sourceName.setError('This source name is already in use');
                            break;
                        case 'slug_in_use':
                            slug.setError('This tracking code is already in use');
                            break;
                        default:
                            notify.error(error || 'Failed to create tracking link');
                            break;
                    }
                } else {
                    onUpdate();
                }
            })
            .catch(() => notify.error('Failed to create tracking link'));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Create Tracking Link</DialogTitle>
            <DialogContent>
                <Typography fontWeight={'bold'}>Tracking Source</Typography>
                <Typography variant="body2" color="textSecondary">
                    Give a name to the source of this tracking link. This could be an affiliate, a
                    marketing campaign, social media platform, etc.
                </Typography>

                <Box mt={2}>
                    {sources.length > 0 && (
                        <ToggleButtonGroup
                            value={sourceMode}
                            exclusive
                            onChange={(_, newValue) => setSourceMode((mode) => newValue || mode)}
                        >
                            <ToggleButton value="existing">Existing Source</ToggleButton>
                            <ToggleButton value="new">New Source</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {sourceMode === 'existing' && (
                        <Dropdown {...source.formProps} label="Tracking Source">
                            <option value="" disabled />
                            {sources.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name}
                                </option>
                            ))}
                        </Dropdown>
                    )}
                    {sourceMode === 'new' && (
                        <TextField {...sourceName.formProps} label="Tracking Source" />
                    )}
                </Box>

                <Typography fontWeight={'bold'} sx={{ mt: 4 }}>
                    Tracking Code
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    This unique code will be added to the event link. It must be unique for this
                    event. Can only contain letters, numbers, and the following special characters:
                    "-", "_", ".", "~". Max 50 characters.
                </Typography>

                <TextField
                    {...slug.formProps}
                    sx={{ mt: 2 }}
                    label="Tracking Code"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Button
                                    color="secondary"
                                    startIcon={<CasinoIcon color="secondary" />}
                                    onClick={randomizeSlug}
                                >
                                    Random
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSave} variant="contained">
                    Create Link
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

export function TrackingLinkEditor(props) {
    const { hostId, eventId, editingLink, onClose, onUpdate } = props;

    const { notify } = useNotification();

    const sourceName = useTextInput({
        defaultValue: editingLink?.sourceName || '',
        maxLength: 50,
    });

    const slug = useTextInput({
        defaultValue: editingLink?.slug || '',
        format: 'urlSafe',
        maxLength: 50,
    });

    const formDisabled =
        sourceName.value === editingLink.sourceName && slug.value === editingLink.slug;

    const randomizeSlug = () => {
        const randomCode = PromoteUtils.generateTrackingCode();
        slug.setValue(randomCode);
    };

    const handleSave = () => {
        let formError = false;

        if (slug.value.length === 0) {
            slug.setError('This field cannot be empty');
            formError = true;
        }

        if (sourceName.value.length === 0) {
            sourceName.setError('This field cannot be empty');
            formError = true;
        }

        if (formError) return;

        PromoteAPI.updateTrackingLink(
            hostId,
            eventId,
            editingLink.sourceId,
            sourceName.value === editingLink.sourceName ? null : sourceName.value,
            slug.value === editingLink.slug ? null : slug.value,
        )
            .then(({ code, error }) => {
                if (error) {
                    switch (code) {
                        case 'source_in_use':
                            sourceName.setError('This source name is already in use');
                            break;
                        case 'slug_in_use':
                            slug.setError('This tracking code is already in use');
                            break;
                        default:
                            notify.error(error || 'Failed to create tracking link');
                            break;
                    }
                } else {
                    onUpdate();
                }
            })
            .catch(() => notify.error('Failed to update tracking link'));
    };

    const handleDelete = () => {
        PromoteAPI.deleteTrackingLink(hostId, eventId, editingLink.sourceId)
            .then(({ error }) => {
                if (error) {
                    notify.error(error);
                } else {
                    onUpdate();
                }
            })
            .catch(() => notify.error('Failed to delete tracking link'));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit Tracking Link</DialogTitle>
            <DialogContent>
                <Typography fontWeight={'bold'}>Tracking Source</Typography>
                <Typography variant="body2" color="textSecondary">
                    If you update the source name, the change will be reflected across all events
                    using this source.
                </Typography>

                <TextField {...sourceName.formProps} sx={{ mt: 2 }} label="Source Name" />

                <Typography fontWeight={'bold'} sx={{ mt: 4 }}>
                    Tracking Code
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Changing the tracking code will generate a new link. Customers can still visit
                    your store page with the old link, but will not be tracked unless they use the
                    new link.
                </Typography>

                <TextField
                    {...slug.formProps}
                    label="Tracking Code"
                    sx={{ mt: 2 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Button
                                    color="secondary"
                                    startIcon={<CasinoIcon color="secondary" />}
                                    onClick={randomizeSlug}
                                >
                                    Random
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSave} variant="contained" disabled={formDisabled}>
                    Save Changes
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

const TrackingLink = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
}));
